'use client'

import { useIsMobile } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { LayoutGrid, List } from 'lucide-react'
import React from 'react'

import { Button } from '@/components/ui'

interface HeadingBarProps extends React.ComponentPropsWithoutRef<'div'> {}

// Product view mode store - external to React component
const productViewModeStore = {
  getSnapshot: (): 'vertical' | 'horizontal' => {
    if (typeof window === 'undefined') return 'vertical'
    const savedMode = localStorage.getItem('productViewMode') as 'vertical' | 'horizontal' | null
    return savedMode || 'vertical'
  },
  getServerSnapshot: (): 'vertical' | 'horizontal' => 'vertical',
  subscribe: (callback: () => void) => {
    const handler = () => callback()
    window.addEventListener('productViewModeChange', handler)
    window.addEventListener('storage', handler)
    return () => {
      window.removeEventListener('productViewModeChange', handler)
      window.removeEventListener('storage', handler)
    }
  },
}

export const HeadingBar = ({ className, ...restProps }: HeadingBarProps) => {
  const isMobile = useIsMobile()
  const productViewMode = React.useSyncExternalStore(
    productViewModeStore.subscribe,
    productViewModeStore.getSnapshot,
    productViewModeStore.getServerSnapshot
  )

  // Initialize default mode on first render if no saved preference
  const isInitialized = React.useRef(false)
  if (typeof window !== 'undefined' && !isInitialized.current) {
    const savedMode = localStorage.getItem('productViewMode')
    if (!savedMode) {
      const defaultMode = isMobile ? 'horizontal' : 'vertical'
      localStorage.setItem('productViewMode', defaultMode)
      window.dispatchEvent(new CustomEvent('productViewModeChange', { detail: defaultMode }))
    }
    isInitialized.current = true
  }

  const handleToggle = React.useCallback((mode: 'vertical' | 'horizontal') => {
    localStorage.setItem('productViewMode', mode)
    window.dispatchEvent(new CustomEvent('productViewModeChange', { detail: mode }))
  }, [])

  return (
    <div
      className={cn(
        'flex h-[var(--heading-bar-height)] shrink-0 items-center justify-between',
        className,
      )}
      {...restProps}
    >
      <h1 className="text-base font-medium">Popular Products</h1>
      <div className="flex items-center gap-2">
        <Button
          variant={productViewMode === 'vertical' ? 'outline' : 'secondary'}
          className="size-10 p-0"
          onClick={() => handleToggle('vertical')}
        >
          <LayoutGrid strokeWidth={1.5} className="size-5" />
        </Button>
        <Button
          variant={productViewMode === 'horizontal' ? 'outline' : 'secondary'}
          className="size-10 p-0"
          onClick={() => handleToggle('horizontal')}
        >
          <List strokeWidth={1.5} className="size-5" />
        </Button>
      </div>
    </div>
  )
}
